import { AREA_GROUPS } from "@/lib/areas";
import { MapPin, AlertTriangle } from "lucide-react";
import { CoverageLists } from "@/components/CoverageLists";

const TONE = {
  gap: "border-coral/40 bg-coral-light text-coral-dark",
  thin: "border-gold/40 bg-gold-light text-gold-dark",
  good: "border-sage/40 bg-sage-light text-sage-dark",
  none: "border-border bg-secondary text-ink-muted",
};
const LABEL = { gap: "Gap", thin: "Thin", good: "Good", none: "No cover" };

export const coverageStatus = (b, c) => (b === 0 ? (c > 0 ? "gap" : "none") : b < c ? "thin" : "good");

export function CoverageView({ staff, clients }) {
  const carers = staff.filter((s) => s.role === "carer");
  const active = clients.filter((c) => c.status !== "inactive");
  const rows = AREA_GROUPS.map(([group, towns]) => [group, towns.map((t) => {
    const b = carers.filter((s) => (s.work_areas || []).includes(t)).length;
    const c = active.filter((x) => x.area === t).length;
    return { town: t, b, c, st: coverageStatus(b, c) };
  })]);
  const flat = rows.flatMap(([, ts]) => ts);
  const gaps = flat.filter((t) => t.st === "gap" || t.st === "thin").sort((a, b) => (a.st === b.st ? b.c - a.c : a.st === "gap" ? -1 : 1));
  const noArea = active.filter((c) => !c.area).length;

  return (
    <div className="space-y-6" data-testid="coverage-view">
      <div className="flex flex-wrap gap-2 text-xs font-semibold">
        {Object.keys(LABEL).map((k) => <span key={k} className={`rounded-full border px-2.5 py-1 ${TONE[k]}`}>{LABEL[k]}</span>)}
        <span className="self-center font-normal text-ink-muted">Gap = clients but no befrienders · Thin = fewer befrienders than clients</span>
      </div>

      <div className="rounded-xl border border-coral/30 bg-white p-5 shadow-sm" data-testid="recruitment-gaps">
        <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-ink"><AlertTriangle size={18} className="text-coral-dark" /> Recruitment gaps</h2>
        {gaps.length === 0
          ? <p className="text-sm text-ink-muted" data-testid="no-gaps">No gaps right now — every area with clients has enough befrienders.</p>
          : <div className="flex flex-wrap gap-2">
              {gaps.map((t) => (
                <span key={t.town} data-testid={`gap-${t.town}`} className={`rounded-lg border px-3 py-1.5 text-sm font-medium ${TONE[t.st]}`}>
                  {t.town} · {t.c} client{t.c === 1 ? "" : "s"}, {t.b} befriender{t.b === 1 ? "" : "s"}
                </span>
              ))}
            </div>}
        {noArea > 0 && <p className="mt-3 text-xs text-ink-muted" data-testid="clients-no-area">{noArea} active client{noArea === 1 ? " has" : "s have"} no area set yet — add it on the client profile to include them here.</p>}
      </div>

      <CoverageLists carers={carers} />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {rows.map(([group, towns]) => (
          <div key={group} className="rounded-xl border border-border bg-white p-4 shadow-sm" data-testid={`coverage-group-${group}`}>
            <h3 className="mb-3 flex items-center gap-1.5 font-heading text-base font-semibold text-ink"><MapPin size={15} className="text-brand" /> {group}</h3>
            <div className="flex flex-wrap gap-1.5">
              {towns.map((t) => (
                <span key={t.town} title={`${t.b} befrienders · ${t.c} active clients`} data-testid={`coverage-town-${t.town}`}
                  className={`rounded-full border px-2.5 py-1 text-xs font-medium ${TONE[t.st]}`}>
                  {t.town} <span className="opacity-75">{t.b}/{t.c}</span>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-ink-muted">Numbers show befrienders / active clients in each town.</p>
    </div>
  );
}
